/**
 * 流水页 **筛选面板**（10 维 · C 域 PRD §5.1.4 · `AC-C1.13`）
 *
 * ## 形态演进
 * | 批次 | 改动 | 依据 |
 * |---|---|---|
 * | 批次 72 | 从「左侧 218px 常驻轨」搬进「吸顶工具条的下拉面板」，成为**全页唯一筛选入口** | `DR-042` · `AC-C1.47` |
 * | 批次 73 | ① 分类由**竖排 Accordion** 改为**横排 chip + 尾部箭头展开 L2**；② 分组改**两列网格**；③ 面板设 `max-height` + 内部滚动 | 负责人：「分类都是竖排，挤占了所有的页面空间」 |
 * | 批次 109 | 维度顺序改为 **账户 → 时间 → 金额区间** 打头（其后分类 / 标签 / 成员 / 方向 / 状态 / 来源） | 负责人：「可以先选账户、日期、金额，最后搜索」 |
 * | 批次 146 | 🆕 **6 个长列表维度（账户 / 分类 / 标签 / 成员 / 状态 / 来源）由 chip 改为多选下拉**；时间与方向仍为 chip，金额仍为两个输入框 | 负责人：「流水筛选页面用下拉列表进行筛选」（`DR-081`） |
 *
 * ## 🔴 为什么只改 6 个维度，不把 10 个全改
 *
 * | 维度 | 形态 | 理由 |
 * |---|---|---|
 * | 账户 / 分类 / 标签 / 成员 / 状态 / 来源 | **多选下拉** | 选项数随时间增长（分类 137 个 L2、账户几十个）⇒ chip 会把面板撑成一面墙 |
 * | 时间（1 个值）· 方向（四选一） | **chip** | 每档都要一眼看全、且**一次点击**选定；做成下拉等于给"点一下"加两次点击 |
 * | 金额区间 | 两个输入框 | 不是枚举 |
 *
 * ## 职责边界（🔴 本组件不做任何判定）
 * 它只做两件事：**把选项画出来** + **把点击翻译成草稿变更**。
 * 于是：多维选择规则、选项文案、请求拼装全部在 `features/journal-*.ts`（可直测），
 * 本文件里没有任何"什么算收入""L1 要不要含 L2"之类的口径 ——
 * 「选 L1 自动含其 L2」**在服务端展开**（`AC-C1.14`），端侧只传用户点的 id。
 *
 * ## ⚠️ 单选 / 多选是**维度性质**，不是样式选择
 * | 维度 | 形态 | 依据 |
 * |---|---|---|
 * | 时间（1） | **单选** | 一个窗口就是一段时间 |
 * | 方向（7） | **单选** | 契约写明「用户每次只表达一个意图」（四值可重叠） |
 * | 其余六个清单维度 | 多选 | §5.1.4「维度内 OR」 |
 *
 * ## ⚠️ 下拉里**看不到选项**是正常的（静态渲染视角）
 * MUI `Select` 的选项渲染在 **Portal 的 Menu 里**，收起时**根本不在 DOM**（铁律 100 的同源现象）。
 * 所以端侧测试只能断言「下拉框在不在 / 选中值显示成什么」，**不能断言选项列表** ——
 * "该显示哪些分类"这件事由纯函数 {@link facetVisibleCategories} 负责，它是可直测的。
 */

import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import type {
  AccountView,
  CategoryView,
  LedgerMemberView,
  TagView,
} from '@jiaying/shared';
import {
  DIRECTION_OPTIONS,
  SOURCE_OPTIONS,
  STATUS_OPTIONS,
  windowOptionsFor,
  type FilterOption,
} from '../features/journal-labels.js';
import {
  JournalWindow,
  type JournalFilterDraft,
} from '../features/journal-filter.js';

export interface JournalFilterRailProps {
  draft: JournalFilterDraft;
  onChange: (next: JournalFilterDraft) => void;
  accounts: readonly AccountView[];
  members: readonly LedgerMemberView[];
  categories: readonly CategoryView[];
  tags: readonly TagView[];
  /** 金额输入的提示（由 `amountInputError()` 算好传进来 —— 本组件不判合法性） */
  amountError: string | null;
  /**
   * 🆕 **用户偏好派生的基线时间档**（`AC-N2.18`）—— 只用来把「默认」标注打在**正确的那一档**上。
   * ⚠️ 本组件**不判**它是哪一档（那是 `windowForPreference()` 的事），只负责转发。
   */
  baseWindow: JournalWindow;
}

/**
 * 一个维度 = 一个「标题 + 内容」格子（两列网格里的单元）。
 *
 * 🔴 为什么用格子而不是「标题一行 + 内容一行」堆叠：宽屏下把 10 个维度**并到两列**，
 * 面板高度直接砍半（实测见 changelog 2026-10-14-b73）。窄屏（`md` 以下）退回单列。
 */
function Field({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * 选中值的展示文案（下拉收起时看到的那一行）。
 *
 * 🔴 为什么不是「已选 N 项」了事：收起时用户要能**认出自己筛了什么** ——
 * 全页面板一收，只有这一行是可见的筛选状态。
 * 超过 2 项才退化成「N 项」（否则长名字会把输入框撑爆）。
 */
function renderSelected(ids: readonly string[], nameOf: (id: string) => string): string {
  if (ids.length === 0) return '不限';
  if (ids.length <= 2) return ids.map(nameOf).join('、');
  return `${String(ids.length)} 项`;
}

/** 一簇可切换的选项（单选 / 多选由 `selected` 的语义决定，本组件不判） */
function Facets<T extends string>({
  options,
  selected,
  onToggle,
  onSelect,
}: {
  options: readonly FilterOption<T>[];
  selected: readonly string[];
  onToggle?: (id: T) => void;
  onSelect?: (id: T) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <Chip
            key={o.id}
            size="small"
            label={o.hint === undefined ? o.label : `${o.label} · ${o.hint}`}
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            data-testid={`facet-${o.id}`}
            data-on={on ? 'true' : 'false'}
            onClick={() => {
              if (onSelect !== undefined) onSelect(o.id);
              else onToggle?.(o.id);
            }}
          />
        );
      })}
    </Box>
  );
}

/**
 * 多选下拉（账户 / 标签 / 成员 / 状态 / 来源 共用）。
 *
 * 🔴 `renderValue` 而不是默认的 chips：默认会把每个选中项画成一枚 chip，
 * 选 8 个账户就把一列撑成 3 行 —— 与"用下拉省空间"的目的正好相反。
 */
function MultiSelect<T extends string>({
  label,
  options,
  selected,
  onChange,
  testId,
}: {
  label: string;
  options: readonly { id: T; label: string }[];
  selected: readonly T[];
  onChange: (next: T[]) => void;
  testId: string;
}) {
  const nameOf = (id: string): string => options.find((o) => o.id === id)?.label ?? id;
  return (
    <TextField
      select
      size="small"
      fullWidth
      label={label}
      value={[...selected]}
      onChange={(e) => {
        const v = e.target.value;
        // 🔴 MUI 多选回传 `string[]`；这里收回泛型（取值域由调用方的 `options` 保证，
        //    不是靠 cast "骗过"类型 —— 但 `Select` 的值类型在 MUI 侧就是 `unknown`）。
        onChange((Array.isArray(v) ? v : String(v).split(',')) as T[]);
      }}
      SelectProps={{
        multiple: true,
        renderValue: (v) => renderSelected(v as string[], nameOf),
        MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
      }}
      inputProps={{ 'data-testid': testId }}
    >
      {options.length === 0 ? (
        // 🔴 空集也要给一句人话：一个点开是空白的下拉比没有更糟
        <MenuItem value="" disabled dense>
          暂无可选项
        </MenuItem>
      ) : (
        options.map((o) => (
          <MenuItem key={o.id} value={o.id} dense>
            {o.label}
          </MenuItem>
        ))
      )}
    </TextField>
  );
}

/**
 * 🆕 批 `112-B`：分类维度**该显示哪些项**（纯函数 —— 交互在静态渲染里点不到，绕 DOM 测是假信号）。
 *
 * 规则两条，第二条的后半段不是贴心而是**诚实**：
 *
 * 1. 未移除的项 ⇒ **恒显示**；
 * 2. 已移除的项 ⇒ 仅当「含已移除」打开，**或它已被选中**时显示。
 *
 * 🔴 为什么"已选中就必须显示"：筛选条件里挂着一个已移除分类、面板却不显示它 ⇒
 * 用户看不到是哪一条，也**取消不了**（只能「清空」全部筛选）。
 * 这是铁律 107 的形态：「对象不在结果里」与「字段为 null」是两种缺失，两种都得能看到。
 */
export function facetVisibleCategories(
  categories: readonly CategoryView[],
  selected: readonly string[],
  showRemoved: boolean,
): CategoryView[] {
  return categories.filter((c) => c.removed !== true || showRemoved || selected.includes(c.id));
}

/** 选项文案：已移除的带后缀（🔴 端侧不改名、不算数，只做**标注**） */
function chipLabel(c: CategoryView): string {
  return c.removed === true ? `${c.name} · 已移除` : c.name;
}

/**
 * 分类维度：**分组多选下拉**（L1 作为 `ListSubheader`，其下 L1 整选 + 各 L2）。
 *
 * | 选项 | 值 | 语义 |
 * |---|---|---|
 * | `{L1}（全部）` | L1 的 id | 选中它 = 该大类全部（**其 L2 由服务端展开**，`AC-C1.14`） |
 * | `{L2}`（缩进） | L2 的 id | 只选这一子类 |
 *
 * 🔴 批 `146` 由 chip 树改为下拉：分类有 **137 个 L2**，chip 形态下必须"点箭头展开一个 L1
 * 才能看到它的子类"，且同时只能开一个 —— 找一个具体的 L2 要点两次、还要先猜它在哪个 L1 下。
 * 分组下拉一次列出全部（可滚动），是 137 个选项唯一现实的形态。
 */
function CategoryMultiSelect({
  categories,
  selected,
  onChange,
}: {
  categories: readonly CategoryView[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
}) {
  /**
   * 🆕 批 `112-B`：「含已移除」开关（`AC-B3.13` 在**筛选侧**的兑现）。
   *
   * 为什么默认关：分类被移除后本来就不该再出现在日常选择里（`AC-B3.14`）；
   * 但**历史流水仍归属它**（软删，`DR-062` D3），所以要给一条"按历史分类筛"的路 ——
   * 否则用户一移除分类，连它历史上花过的钱都搜不到了。
   *
   * 状态是**组件内**的（不进 `JournalFilterDraft`）：它只影响"面板列出什么"，
   * 不影响"筛什么"，进 draft 会污染查询参数与 URL。
   */
  const [showRemoved, setShowRemoved] = useState(false);
  const removedCount = categories.filter((c) => c.removed === true).length;
  const shown = facetVisibleCategories(categories, selected, showRemoved);
  const l1 = shown.filter((c) => c.level === 1);

  const nameOf = (id: string): string => {
    const row = shown.find((c) => c.id === id) ?? categories.find((c) => c.id === id);
    return row === undefined ? id : chipLabel(row);
  };

  return (
    <Box data-testid="facet-cat-group">
      <TextField
        select
        size="small"
        fullWidth
        label="分类"
        value={[...selected]}
        onChange={(e) => {
          const v = e.target.value;
          onChange(Array.isArray(v) ? (v as string[]) : String(v).split(','));
        }}
        SelectProps={{
          multiple: true,
          renderValue: (v) => renderSelected(v as string[], nameOf),
          MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
        }}
        inputProps={{ 'data-testid': 'facet-cat' }}
      >
        {l1.map((parent) => {
          // 🔴 必须用 `shown` 而不是 `categories`：已移除的子类在开关关着时不在列表里。
          const children = shown.filter((c) => c.parentId === parent.id);
          return [
            <ListSubheader key={`h-${parent.id}`} disableSticky>
              {parent.name}
            </ListSubheader>,
            <MenuItem key={parent.id} value={parent.id} dense>
              {parent.name}（全部）
            </MenuItem>,
            ...children.map((child) => (
              <MenuItem key={child.id} value={child.id} dense sx={{ pl: 4 }}>
                {chipLabel(child)}
              </MenuItem>
            )),
          ];
        })}
      </TextField>
      {/*
        🆕 批 `112-B`：只有**确实存在已移除分类**时才出现这个开关 ——
        一个永远点不出东西的开关比没有更糟（同 110 那次删掉「更多分类」的理由）。
        🔴 `data-testid` 挂在 label 的 span 上：MUI 6 的 `Checkbox.inputProps` 类型不接 `data-*`（铁律 100）。
      */}
      {removedCount > 0 && (
        <FormControlLabel
          data-testid="facet-cat-removed-row"
          sx={{ mt: 0.25, ml: 0, '& .MuiFormControlLabel-label': { fontSize: 12 } }}
          control={
            <Checkbox
              size="small"
              checked={showRemoved}
              onChange={(e) => {
                setShowRemoved(e.target.checked);
              }}
            />
          }
          label={<span data-testid="facet-cat-show-removed">{`含已移除（${String(removedCount)}）`}</span>}
        />
      )}
    </Box>
  );
}

export function JournalFilterRail({
  draft,
  onChange,
  accounts,
  members,
  categories,
  tags,
  amountError,
  baseWindow,
}: JournalFilterRailProps) {
  const patch = (part: Partial<JournalFilterDraft>) => {
    onChange({ ...draft, ...part });
  };

  return (
    <Box data-testid="journal-filter-rail">
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          筛选
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/*
          「清空」= 回到默认态（只开近 3 月 · `AC-C1.17`）。
          🔴 不是"把所有维度设空" —— 那会把默认窗口一起去掉（变成"不限时间"）。
        */}
        <Chip
          size="small"
          variant="outlined"
          label="清空"
          data-testid="filter-clear"
          onClick={() => {
            patch({
              window: JournalWindow.Recent3m,
              from: '',
              to: '',
              accountIds: [],
              memberIds: [],
              categoryIds: [],
              tagIds: [],
              amountMinText: '',
              amountMaxText: '',
              direction: '',
              statuses: [],
              sources: [],
              keyword: '',
            });
          }}
        />
      </Stack>
      <Divider />

      {/* 🔴 2026-10-14 形态订正：原「标题一行 + 内容一行」的**竖排堆叠**改为**两列网格**（`md` 以下退回单列）。 */}
      <Box
        data-testid="facet-grid"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          columnGap: 2.5,
          alignItems: 'start',
          mt: 1,
        }}
      >
        <Field title="账户">
          <MultiSelect
            label="账户"
            options={accounts.map((a) => ({ id: a.id, label: a.name }))}
            selected={draft.accountIds}
            onChange={(next) => {
              patch({ accountIds: next });
            }}
            testId="facet-account"
          />
        </Field>

        <Field title="时间">
          <Facets
            options={windowOptionsFor(baseWindow)}
            selected={[draft.window]}
            onSelect={(id) => {
              patch({ window: id });
            }}
          />
          {draft.window === JournalWindow.Custom && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
              <TextField
                size="small"
                type="date"
                label="起"
                value={draft.from}
                onChange={(e) => {
                  patch({ from: e.target.value });
                }}
                inputProps={{ 'data-testid': 'filter-from' }}
              />
              <TextField
                size="small"
                type="date"
                label="止"
                value={draft.to}
                onChange={(e) => {
                  patch({ to: e.target.value });
                }}
                inputProps={{ 'data-testid': 'filter-to' }}
              />
            </Stack>
          )}
        </Field>

        <Field title="金额区间">
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="最小"
              value={draft.amountMinText}
              onChange={(e) => {
                patch({ amountMinText: e.target.value });
              }}
              inputProps={{ 'data-testid': 'filter-amount-min' }}
            />
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
            <TextField
              size="small"
              placeholder="最大"
              value={draft.amountMaxText}
              onChange={(e) => {
                patch({ amountMaxText: e.target.value });
              }}
              inputProps={{ 'data-testid': 'filter-amount-max' }}
            />
          </Stack>
          {amountError !== null && (
            <Typography variant="caption" color="error" data-testid="filter-amount-error">
              {amountError}
            </Typography>
          )}
        </Field>

        {/* 分类自带标题（`Field` 的 title 置空，避免"分类 / 分类"两次） */}
        <Box sx={{ mb: 1.25 }}>
          <CategoryMultiSelect
            categories={categories}
            selected={draft.categoryIds}
            onChange={(next) => {
              patch({ categoryIds: next });
            }}
          />
        </Box>

        <Field title="标签">
          <MultiSelect
            label="标签"
            options={tags.map((t) => ({ id: t.id, label: t.name }))}
            selected={draft.tagIds}
            onChange={(next) => {
              patch({ tagIds: next });
            }}
            testId="facet-tag"
          />
        </Field>

        <Field title="成员">
          <MultiSelect
            label="成员"
            options={members.map((m) => ({ id: m.userId, label: m.name }))}
            selected={draft.memberIds}
            onChange={(next) => {
              patch({ memberIds: next });
            }}
            testId="facet-member"
          />
        </Field>

        <Field title="方向">
          <Facets
            options={DIRECTION_OPTIONS}
            selected={draft.direction === '' ? [] : [draft.direction]}
            onSelect={(id) => {
              // 🔴 再点一次 = 取消（单选维度必须能回到"不限"，否则用户被锁死在一个方向上）
              patch({ direction: draft.direction === id ? '' : id });
            }}
          />
        </Field>

        <Field title="状态">
          <MultiSelect
            label="状态"
            options={STATUS_OPTIONS.map((o) => ({ id: o.id, label: o.label }))}
            selected={draft.statuses}
            onChange={(next) => {
              patch({ statuses: next });
            }}
            testId="facet-status"
          />
        </Field>

        <Field title="来源">
          <MultiSelect
            label="来源"
            options={SOURCE_OPTIONS.map((o) => ({ id: o.id, label: o.label }))}
            selected={draft.sources}
            onChange={(next) => {
              patch({ sources: next });
            }}
            testId="facet-source"
          />
        </Field>
      </Box>
    </Box>
  );
}
